'use client'

import { cn } from '@/utilities/ui'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, ChevronUp } from 'lucide-react'
import * as React from 'react'

const Select: React.FC<React.ComponentProps<typeof SelectPrimitive.Root>> = (props) => {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

const SelectGroup: React.FC<React.ComponentProps<typeof SelectPrimitive.Group>> = ({
  className,
  ...props
}) => {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      // Figma Menu Group (2198:40024): theme/background fill, 1px theme/border
      // bottom divider, p-1 (4px). The Menu container itself (2198:40023) has no
      // padding — the 4px lives on the group. Our Viewport carries that 4px so
      // that ungrouped menus (every current call site) stay correct, so a group
      // pulls itself back out to the panel edge and re-applies its own p-1. Net
      // effect: identical inset for the items, a full-bleed divider, and Figma's
      // 8px gap between stacked groups.
      className={cn(
        'bg-background border-border -mx-1 flex flex-col items-start border-b p-1 first:-mt-1 last:-mb-1',
        className,
      )}
      {...props}
    />
  )
}

const SelectValue: React.FC<React.ComponentProps<typeof SelectPrimitive.Value>> = (props) => {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

const SelectTrigger: React.FC<React.ComponentProps<typeof SelectPrimitive.Trigger>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        // Wilson Property canonical SelectTrigger — Figma Trigger component set
        // 2198:36838 / Input select instance 2198:40022: fixed 28px tall, fill
        // theme/muted #e0dfe0, NO border, radius sm (2px), px-3 / py-2, shadow/xs,
        // Body/bodySm 14/16 weight 300 in theme/foreground (no dimmed placeholder
        // state is defined).
        //   Hover    (2198:36842) fill flips to theme/secondary #74cdde.
        //   Focus    (2198:36845) carries ONLY the focus effect — 3px theme/ring.
        //            shadow/xs is dropped in that variant, hence focus-visible:shadow-none.
        //   Error    (2198:36848) 1px theme/destructive; the box stays 28px because
        //            h-7 is a fixed height and the box is border-box.
        //   Disabled (2198:36851) opacity 50%, fill stays theme/muted.
        "flex h-7 w-full items-center justify-between rounded-sm bg-muted px-3 py-2 text-body-sm font-light text-foreground shadow-xs transition-[color,background-color,box-shadow] not-disabled:hover:bg-secondary focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border aria-invalid:border-destructive *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        {/* icon/icon-primary #000 at full opacity — node 2198:36841 carries no
            opacity of its own (the Disabled variant dims the whole trigger). */}
        <ChevronDown className="size-4" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

const SelectContent: React.FC<React.ComponentProps<typeof SelectPrimitive.Content>> = ({
  children,
  className,
  position = 'popper',
  ...props
}) => {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          // Figma Menu (2198:40023 / 2198:40033): theme/background #ffffff fill,
          // 1px theme/border outline, radius sm (2px).
          'bg-background text-foreground border-border rounded-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

const SelectLabel: React.FC<React.ComponentProps<typeof SelectPrimitive.Label>> = ({
  className,
  ...props
}) => {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      // Figma Menu Group Heading (2198:36935 / 2198:36937) — Body/bodyXsAlt
      // 12/14 weight 400, UPPERCASE, theme/muted-foreground, h 32, radius sm.
      className={cn(
        'flex h-8 items-center rounded-sm px-2 py-1.5 text-body-xs font-normal uppercase text-muted-foreground',
        className,
      )}
      {...props}
    />
  )
}

const SelectItem: React.FC<React.ComponentProps<typeof SelectPrimitive.Item>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      // Figma Menu Item (2198:36854 set / 2198:40026–40030 / 2198:40036–40047):
      // symmetric px 8, py 6, gap 8, fixed h 32, radius sm, no trailing check glyph.
      // Label = Body/bodySm 14/16 weight 300 in theme/foreground.
      // Hover/highlighted (2198:36860) fills with theme/secondary #74cdde — NOT the
      // accent green — and the label colour does not change. Icons are
      // icon/icon-primary #000.
      className={cn(
        "focus:bg-secondary focus:text-secondary-foreground [&_svg:not([class*='text-'])]:text-foreground relative flex h-8 w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-body-sm font-light outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

const SelectSeparator: React.FC<React.ComponentProps<typeof SelectPrimitive.Separator>> = ({
  className,
  ...props
}) => {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

const SelectScrollUpButton: React.FC<
  React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
> = ({ className, ...props }) => {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUp className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

const SelectScrollDownButton: React.FC<
  React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>
> = ({ className, ...props }) => {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDown className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
